import React, { useRef } from 'react';
import { gsap, useGSAP } from '@/lib/gsap-config';
import { cn } from '@/lib/utils';

interface LogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function Logo({ className, size = 'md' }: LogoProps) {
  const containerRef = useRef<HTMLAnchorElement>(null);
  const slideBackRef = useRef<SVGRectElement>(null);
  const slideFrontRef = useRef<SVGRectElement>(null);
  const sparkRef = useRef<SVGCircleElement>(null);
  const pathRef = useRef<SVGPathElement>(null);

  const { contextSafe } = useGSAP({ scope: containerRef });

  // Continuous subtle micro-motion
  useGSAP(() => {
    // Subtle breathing/floating for the front slide layer
    if (slideFrontRef.current) {
      gsap.to(slideFrontRef.current, {
        y: -1.5,
        x: 1,
        duration: 2.4,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    }

    // Spark gentle pulse
    if (sparkRef.current) {
      gsap.to(sparkRef.current, {
        scale: 1.25,
        transformOrigin: 'center center',
        duration: 1.8,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    }
  }, { scope: containerRef });

  // Interactive Hover Animation
  const handleMouseEnter = contextSafe(() => {
    if (slideFrontRef.current && slideBackRef.current && pathRef.current) {
      gsap.to(slideFrontRef.current, {
        x: 3,
        y: -3,
        duration: 0.35,
        ease: 'power2.out',
      });
      gsap.to(slideBackRef.current, {
        x: -1,
        y: 1,
        opacity: 0.8,
        duration: 0.35,
        ease: 'power2.out',
      });
      gsap.to(pathRef.current, {
        drawSVG: '0% 100%',
        duration: 0.45,
        ease: 'power2.out',
      });
    }
  });

  const handleMouseLeave = contextSafe(() => {
    if (slideFrontRef.current && slideBackRef.current) {
      gsap.to(slideFrontRef.current, {
        x: 0,
        y: 0,
        duration: 0.45,
        ease: 'power2.out',
      });
      gsap.to(slideBackRef.current, {
        x: 0,
        y: 0,
        opacity: 0.5,
        duration: 0.45,
        ease: 'power2.out',
      });
    }
  });

  const iconSizes = {
    sm: 'w-6 h-6',
    md: 'w-7 h-7',
    lg: 'w-8 h-8',
  };

  const textSizes = {
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-xl',
  };

  return (
    <a
      ref={containerRef}
      href="/"
      aria-label="Làm Slide"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn('inline-flex items-center gap-2.5 group select-none cursor-pointer', className)}
    >
      {/* Animated SVG Presentation Slide Icon */}
      <div className={cn('relative shrink-0 flex items-center justify-center', iconSizes[size])}>
        <svg viewBox="0 0 32 32" fill="none" className="w-full h-full">
          {/* Back slide frame (underneath) */}
          <rect
            ref={slideBackRef}
            x="3"
            y="9"
            width="22"
            height="16"
            rx="3"
            className="stroke-muted-foreground/50 fill-muted/40"
            strokeWidth="1.5"
            style={{ opacity: 0.5 }}
          />

          {/* Front primary slide frame */}
          <rect
            ref={slideFrontRef}
            x="7"
            y="5"
            width="22"
            height="16"
            rx="3"
            className="stroke-foreground fill-card shadow-sm"
            strokeWidth="1.5"
          />

          {/* Presentation content line inside front slide */}
          <path
            ref={pathRef}
            d="M 11,10 L 21,10 M 11,14 L 17,14"
            className="stroke-brand"
            strokeWidth="1.5"
            strokeLinecap="round"
          />

          {/* AI Energy Spark Indicator */}
          <circle
            ref={sparkRef}
            cx="24"
            cy="14"
            r="1.8"
            className="fill-brand"
          />
        </svg>
      </div>

      {/* Brand Text: Just "lamslide" */}
      <span className={cn('brand-wordmark text-foreground font-sans', textSizes[size])}>
        Làm <span className="brand-wordmark-accent">Slide</span>
      </span>
    </a>
  );
}

export default Logo;
